/**
 * The forgot-password form, in place, for a SIGNED-IN user.
 *
 * Settings → Password offers "Forgot your current password?" for the one person
 * the change-password form cannot help: someone who holds a session but not the
 * password it would ask them to confirm. The obvious wiring —
 * `navigate('/forgot-password')` — is wrong in three compounding ways, and all
 * three are invisible until you are the user:
 *
 *  1. **It unmounts the entire app.** `/forgot-password` is declared before the
 *     `/*` catch-all, so React Router drops `RootShell`. The desktop shell keeps
 *     every deck mounted for the life of the tab precisely so switching is
 *     instant and scroll, open panels and the live chat session survive; a
 *     route out of the shell throws all of it away to show one email field.
 *  2. **It lands them on anonymous chrome.** `ForgotPasswordPage` wraps the form
 *     in `AuthLayout`, whose only exit is "Back to sign in" — no signed-in
 *     redirect, so the way out of a password screen appears to be signing out.
 *  3. **It can strand them.** The web app ships
 *     `apple-mobile-web-app-capable="yes"`, so an iOS home-screen install has no
 *     browser chrome and no back button. Off the shell, with only a sign-in
 *     link, the remaining move is to force-quit the app.
 *
 * A modal keeps them where they are, which is the pattern this codebase already
 * reached for: `AuthPromptModal` switches to `ForgotPasswordForm` in place
 * rather than navigating, for the same reason (a share recipient must not lose
 * their place). This is the signed-in sibling of that — no sign-in or sign-up
 * views, since the user is already authenticated and offering them would be
 * nonsense.
 *
 * WHY NOT REUSE `AuthPromptModal` DIRECTLY: its footer links out to Sign
 * up / Back to sign in, both wrong here, and it requires an `onSuccess`
 * sign-in callback that has no meaning for someone already signed in.
 */
import { Modal } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { ForgotPasswordForm } from './ForgotPasswordForm'

export function ForgotPasswordModal({ email, onClose }: {
  /** The signed-in user's address, prefilled into the form. */
  email?: string
  onClose: () => void
}) {
  const { t } = useTranslation('auth')
  return (
    // Existing `auth:forgotPassword.*` keys only — all four locales already
    // carry them, so this adds no untranslated string. `ForgotPasswordForm`
    // renders its own description, and passing one here too would print the
    // same sentence twice.
    <Modal
      title={t('forgotPassword.title')}
      onClose={onClose}
      maxWidth="max-w-md"
      // A CLOSE BUTTON IS LOAD-BEARING HERE, not decoration. On success the form
      // replaces itself with an icon and two lines of text — no input, no
      // button, nothing focusable. `Modal`'s focus trap takes its
      // `items.length === 0` branch and `preventDefault()`s every Tab and
      // Shift+Tab while the app root is `inert`, so without this the terminal
      // state has exactly one exit: Escape on desktop, and on mobile — which has
      // no Escape key — an unsignposted tap on the dim backdrop. That is the
      // same stranding this modal exists to prevent, landing on the iOS
      // home-screen install named in the argument for it.
      footer={(
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-md themed-btn-secondary"
        >
          {t('common:close')}
        </button>
      )}
    >
      {/* `Modal` renders `{children}` raw between its own padded header and
          footer — it supplies no body padding, and every other caller adds its
          own. Without this the description, label, input and full-width submit
          button all sit flush against the panel's edges. */}
      <div className="px-5 py-4">
        <ForgotPasswordForm defaultEmail={email} />
      </div>
    </Modal>
  )
}
